import { lazy, Suspense } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { Provider } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { AppLayout } from '@/components/layout/AppLayout'
import { ProtectedRoute } from '@/components/routes/ProtectedRoute'
import { PublicRoute } from '@/components/routes/PublicRoute'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { FullPageLoader } from '@/components/shared/LoadingSpinner'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AbilityProvider } from '@/contexts/AbilityContext'
import { queryClient } from '@/hooks/useAuth'
import { store } from '@/store'

import { APP_ROUTES, ROUTE_PERMISSIONS } from './libs/constants'

// Auth pages
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'))
const Login = lazy(() => import('./pages/auth/Login'))
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'))

// Main pages
const Billing = lazy(() => import('./pages/Billing'))
const Booking = lazy(() => import('./pages/booking'))
const NewBooking = lazy(() => import('./pages/booking/NewBooking'))
const Campaigns = lazy(() => import('./pages/Campaigns'))
const CategoriesPage = lazy(() => import('./pages/categories'))
const CategoryDetails = lazy(() => import('./pages/categories/CategoryDetails'))
const NewCategory = lazy(() => import('./pages/categories/NewCategory'))
const CommunicationPage = lazy(() => import('./pages/communication'))
const TemplateDetails = lazy(() => import('./pages/communication/TemplateDetails'))
const TemplateEditor = lazy(() => import('./pages/communication/TemplateEditor'))
const CompanyDetails = lazy(() => import('./pages/customers/CompanyDetails'))
const CustomerDetails = lazy(() => import('./pages/customers/CustomerDetails'))
const CustomersPage = lazy(() => import('./pages/customers/index'))
const NewCompany = lazy(() => import('./pages/customers/NewCompany'))
const NewCustomer = lazy(() => import('./pages/customers/NewCustomer'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const JobDetails = lazy(() => import('./pages/jobs/JobDetails'))
const Jobs = lazy(() => import('./pages/jobs/Jobs'))
const Leaves = lazy(() => import('./pages/Leaves'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Products = lazy(() => import('./pages/products/index'))
const NewProduct = lazy(() => import('./pages/products/NewProduct'))
const ProductDetails = lazy(() => import('./pages/products/ProductDetails'))
const RentalFleet = lazy(() => import('./pages/rental-fleet'))
const NewRentalVehicle = lazy(() => import('./pages/rental-fleet/NewRentalVehicle'))
const RentalVehicleDetails = lazy(() => import('./pages/rental-fleet/RentalVehicleDetails'))
const Services = lazy(() => import('./pages/services/index'))
const NewService = lazy(() => import('./pages/services/NewService'))
const ServiceDetails = lazy(() => import('./pages/services/ServiceDetails'))
const BranchInfo = lazy(() => import('./pages/settings/BranchInfo'))
const Permissions = lazy(() => import('./pages/settings/Permissions'))
const Qualifications = lazy(() => import('./pages/settings/qualifications'))
const NewQualification = lazy(() => import('./pages/settings/qualifications/NewQualification'))
const QualificationDetails = lazy(
  () => import('./pages/settings/qualifications/QualificationDetails'),
)
const SettingsLayout = lazy(() => import('./pages/settings/SettingsLayout'))
const Staff = lazy(() => import('./pages/settings/Staff'))
const StaffDetails = lazy(() => import('./pages/settings/staff/StaffDetails'))
const NewStore = lazy(() => import('./pages/settings/stores/NewStore'))
const StoreDetails = lazy(() => import('./pages/settings/stores/StoreDetails'))
const Stores = lazy(() => import('./pages/settings/stores/Stores'))
const WeeklyNotes = lazy(() => import('./pages/settings/WeeklyNotes'))
const WorkingHours = lazy(() => import('./pages/settings/WorkingHours'))
const Account = lazy(() => import('./pages/user/Account'))
const NewVehicle = lazy(() => import('./pages/vehicles/NewVehicle'))
const VehicleDetails = lazy(() => import('./pages/vehicles/VehicleDetails'))
const Vehicles = lazy(() => import('./pages/vehicles/Vehicles'))
const WorkLogs = lazy(() => import('./pages/WorkLogs'))

queryClient.setDefaultOptions({
  queries: {
    retry: 1,
    refetchOnWindowFocus: false,
  },
})

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <AbilityProvider>
        <TooltipProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Suspense fallback={<FullPageLoader />}>
              <Routes>
                {/* Auth pages - public only */}
                <Route
                  path={APP_ROUTES.auth.login}
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path={APP_ROUTES.auth.forgotPassword}
                  element={
                    <PublicRoute>
                      <ForgotPassword />
                    </PublicRoute>
                  }
                />
                <Route
                  path={APP_ROUTES.auth.resetPassword}
                  element={
                    <PublicRoute>
                      <ResetPassword />
                    </PublicRoute>
                  }
                />

                {/* Protected app pages */}

                <Route element={<ProtectedRoute />}>
                  <Route element={<AppLayout />}>
                    {/* Dashboard */}
                    <Route index element={<Dashboard />} />

                    {/* Customers */}
                    <Route
                      path={APP_ROUTES.customer.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                          <CustomersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.customer.new}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.customers.create}>
                          <NewCustomer />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.customer.companyNew}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.customers.create}>
                          <NewCompany />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.customer.base}/companies/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                          <CompanyDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.customer.base}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.customers.read}>
                          <CustomerDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Vehicles */}
                    <Route
                      path={APP_ROUTES.vehicle.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
                          <Vehicles />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.vehicle.new}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.create}>
                          <NewVehicle />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.vehicle.base}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.vehicles.read}>
                          <VehicleDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Services */}
                    <Route
                      path={APP_ROUTES.service.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.services.read}>
                          <Services />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.service.new}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.services.create}>
                          <NewService />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.service.base}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.services.read}>
                          <ServiceDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Jobs */}
                    <Route
                      path={APP_ROUTES.job.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
                          <Jobs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.job.base}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.jobs.read}>
                          <JobDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Booking */}
                    <Route
                      path={APP_ROUTES.booking.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.booking.create}>
                          <Booking />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.booking.new}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.booking.create}>
                          <NewBooking />
                        </ProtectedRoute>
                      }
                    />

                    {/* Others */}
                    <Route
                      path={APP_ROUTES.leaves}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.leaves.read}>
                          <Leaves />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.workLogs}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.workLogs.read}>
                          <WorkLogs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.billing}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.billing.read}>
                          <Billing />
                        </ProtectedRoute>
                      }
                    />

                    {/* Products */}
                    <Route
                      path={APP_ROUTES.product.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
                          <Products />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.product.new}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.products.create}>
                          <NewProduct />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.product.base}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.products.read}>
                          <ProductDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Rental */}
                    <Route
                      path={APP_ROUTES.rentCar.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
                          <RentalFleet />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.rentCar.new}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.create}>
                          <NewRentalVehicle />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.rentCar.base}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.rentalFleet.read}>
                          <RentalVehicleDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Campaigns */}
                    <Route
                      path={APP_ROUTES.campaigns}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.campaigns.read}>
                          <Campaigns />
                        </ProtectedRoute>
                      }
                    />

                    {/* Categories */}
                    <Route
                      path={APP_ROUTES.category.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
                          <CategoriesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.category.create('service')}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.categories.create}>
                          <NewCategory type="service" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.category.create('product')}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.categories.create}>
                          <NewCategory type="product" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.category.type('service')}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
                          <CategoryDetails type="service" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.category.type('product')}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.categories.read}>
                          <CategoryDetails type="product" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Communication */}
                    <Route
                      path={APP_ROUTES.communication.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
                          <CommunicationPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.communication.create('email')}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.communication.create}>
                          <TemplateEditor type="email" mode="create" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={APP_ROUTES.communication.create('sms')}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.communication.create}>
                          <TemplateEditor type="sms" mode="create" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.communication.type('email')}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
                          <TemplateDetails type="email" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path={`${APP_ROUTES.communication.type('sms')}/:id`}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.communication.read}>
                          <TemplateDetails type="sms" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Settings */}
                    <Route
                      path={APP_ROUTES.settings.base}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.settings.read}>
                          <SettingsLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={<Navigate to={APP_ROUTES.settings.branchInfo} replace />}
                      />
                      <Route path={APP_ROUTES.settings.branchInfo} element={<BranchInfo />} />
                      <Route path={APP_ROUTES.settings.workingHours} element={<WorkingHours />} />
                      <Route path={APP_ROUTES.settings.staff.base} element={<Staff />} />
                      <Route
                        path={`${APP_ROUTES.settings.staff.base}/:id`}
                        element={<StaffDetails />}
                      />
                      <Route path={APP_ROUTES.settings.permissions} element={<Permissions />} />
                      <Route path={APP_ROUTES.settings.stores.base} element={<Stores />} />
                      <Route path={APP_ROUTES.settings.stores.new} element={<NewStore />} />
                      <Route
                        path={`${APP_ROUTES.settings.stores.base}/:id`}
                        element={<StoreDetails />}
                      />
                      <Route
                        path={APP_ROUTES.settings.qualifications.base}
                        element={<Qualifications />}
                      />
                      <Route
                        path={`${APP_ROUTES.settings.qualifications.base}/new`}
                        element={<NewQualification />}
                      />
                      <Route
                        path={`${APP_ROUTES.settings.qualifications.base}/:id`}
                        element={<QualificationDetails />}
                      />
                      <Route path={APP_ROUTES.settings.weeklyNotes} element={<WeeklyNotes />} />
                      {/* <Route path="integrations" element={<Integrations />} /> */}
                    </Route>

                    {/* Account */}
                    <Route
                      path={APP_ROUTES.account}
                      element={
                        <ProtectedRoute {...ROUTE_PERMISSIONS.user.read}>
                          <Account />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                </Route>

                {/* Fallback */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </AbilityProvider>
    </QueryClientProvider>
  </Provider>
)

export default App
